import React, { useState } from 'react';
import { useAuditLogs, useResourceHistory } from '../hooks/useAudit';
import { AuditLog } from '../services/audit';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Eye, 
  Filter, 
  Calendar,
  User,
  Activity,
  Building,
  Package,
  Archive,
  RotateCcw,
  Move,
  Upload,
  Download as DownloadIcon
} from 'lucide-react';

interface AuditLogsProps {
  resourceType?: string;
  resourceId?: string;
  showFilters?: boolean;
  maxHeight?: string;
}

const AuditLogs: React.FC<AuditLogsProps> = ({ 
  resourceType, 
  resourceId, 
  showFilters = true,
  maxHeight = '400px'
}) => {
  const [filters, setFilters] = useState({
    action: '',
    startDate: '',
    endDate: '',
    scope: ''
  });

  // Utiliser le hook approprié selon le contexte
  const { data: auditLogs, isLoading, error } = resourceType && resourceId 
    ? useResourceHistory(resourceType, resourceId)
    : useAuditLogs(filters);

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE': return <Activity className="h-4 w-4 text-green-500" />;
      case 'UPDATE': return <Eye className="h-4 w-4 text-blue-500" />;
      case 'DELETE': return <Archive className="h-4 w-4 text-red-500" />;
      case 'ARCHIVE': return <Archive className="h-4 w-4 text-orange-500" />;
      case 'RESTORE': return <RotateCcw className="h-4 w-4 text-purple-500" />;
      case 'MOVE': return <Move className="h-4 w-4 text-indigo-500" />;
      case 'EXPORT': return <DownloadIcon className="h-4 w-4 text-cyan-500" />;
      case 'IMPORT': return <Upload className="h-4 w-4 text-teal-500" />;
      default: return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'CREATE': return 'Création';
      case 'UPDATE': return 'Modification';
      case 'DELETE': return 'Suppression';
      case 'ARCHIVE': return 'Archivage';
      case 'RESTORE': return 'Restauration';
      case 'MOVE': return 'Déplacement';
      case 'EXPORT': return 'Export';
      case 'IMPORT': return 'Import';
      default: return action;
    }
  };

  const getResourceIcon = (resourceType: string) => {
    switch (resourceType) {
      case 'SITE': return <Building className="h-4 w-4 text-blue-600" />;
      case 'BUILDING': return <Building className="h-4 w-4 text-green-600" />;
      case 'PIECE': return <Building className="h-4 w-4 text-purple-600" />;
      case 'ZONE': return <Package className="h-4 w-4 text-orange-600" />;
      case 'BOX': return <Package className="h-4 w-4 text-indigo-600" />;
      case 'ITEM': return <Package className="h-4 w-4 text-cyan-600" />;
      case 'USER': return <User className="h-4 w-4 text-red-600" />;
      default: return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'UI': return 'Interface Web';
      case 'MOBILE': return 'Application Mobile';
      case 'API': return 'API';
      case 'IMPORT': return 'Import';
      default: return source;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Chargement des logs d'audit...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <h3 className="text-red-800 font-medium">Erreur de chargement</h3>
        <p className="text-red-600 mt-1">
          Impossible de charger les logs d'audit. Vérifiez votre connexion.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Explication */}
      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <p className="text-sm text-purple-800">
          📊 L'<strong>audit</strong> trace toutes vos actions dans le système (création, modification, suppression, export, déplacement). 
          Cet historique est <strong>immutable</strong> et conforme <strong>RGPD</strong>.
        </p>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">Filtres</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Action
              </label>
              <select
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Toutes les actions</option>
                <option value="CREATE">Création</option>
                <option value="UPDATE">Modification</option>
                <option value="DELETE">Suppression</option>
                <option value="ARCHIVE">Archivage</option>
                <option value="RESTORE">Restauration</option>
                <option value="MOVE">Déplacement</option>
                <option value="EXPORT">Export</option>
                <option value="IMPORT">Import</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de début
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scope
              </label>
              <input
                type="text"
                value={filters.scope}
                onChange={(e) => setFilters({ ...filters, scope: e.target.value })}
                placeholder="Filtrer par scope"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Liste des logs */}
      <div 
        className="bg-white border border-gray-200 rounded-lg overflow-hidden"
        style={{ maxHeight, overflowY: 'auto' }}
      >
        {auditLogs && auditLogs.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {auditLogs.map((log: AuditLog) => (
              <div key={log.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    {getActionIcon(log.action)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getResourceIcon(log.resourceType)}
                        <span className="text-sm font-medium text-gray-900">
                          {getActionLabel(log.action)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {log.resourceType.toLowerCase()}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm', { locale: fr })}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <User className="h-4 w-4" />
                        <span>Utilisateur: {log.actorId}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Activity className="h-4 w-4" />
                        <span>Source: {getSourceLabel(log.source)}</span>
                      </div>
                      
                      {log.scope && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Building className="h-4 w-4" />
                          <span>Scope: {log.scope}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Détails des changements */}
                    {(log.previousValue || log.newValue) && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Détails des changements</h4>
                        
                        {log.previousValue && (
                          <div className="mb-2">
                            <span className="text-xs font-medium text-red-600">Avant:</span>
                            <pre className="text-xs text-gray-600 mt-1 bg-red-50 p-2 rounded">
                              {JSON.stringify(log.previousValue, null, 2)}
                            </pre>
                          </div>
                        )}
                        
                        {log.newValue && (
                          <div>
                            <span className="text-xs font-medium text-green-600">Après:</span>
                            <pre className="text-xs text-gray-600 mt-1 bg-green-50 p-2 rounded">
                              {JSON.stringify(log.newValue, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Aucun log d'audit trouvé</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
